import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SubscriptionRequiredModal from '../../components/ui/SubscriptionRequiredModal';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { isSubscriptionError } from '../../lib/subscriptionGate';
import { disablePush, enablePush, getPushEnabledState, isPushSupported } from '../../lib/push';
import {
  type NotificationItem,
  deleteNotifications,
  fetchNotificationsPaged,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../lib/teacherApi';

type TabKey = 'all' | 'unread';

type ContactUser = {
  id: number;
  full_name: string;
  email: string;
  role: string;
  school_id: number | null;
};

type ContactAttachment = {
  original_name: string;
  stored_name: string;
  content_type: string | null;
  size: number;
};

type ContactSubmissionDetail = {
  id: number;
  category: string;
  subject: string;
  message: string;
  contact_email: string | null;
  created_at: string;
  attachments: ContactAttachment[];
  user: ContactUser | null;
};

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ro-RO');
  } catch {
    return String(iso);
  }
}

function fmtBytes(n: number) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let u = 0;
  let x = v;
  while (x >= 1024 && u < units.length - 1) {
    x /= 1024;
    u += 1;
  }
  return `${x.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

function extractContactSubmissionId(n: NotificationItem): number | null {
  const content = String((n as any)?.content || '');
  const m = content.match(/#(\d+)/);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] bg-card border border-border rounded-xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div className="text-lg font-semibold text-foreground">{title}</div>
          <button
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
            onClick={onClose}
            aria-label="Inchide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { me, uiRole, signOut } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('all');
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pushEnabled, setPushEnabled] = useState<boolean>(false);
  const [pushBusy, setPushBusy] = useState<boolean>(false);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactDetail, setContactDetail] = useState<ContactSubmissionDetail | null>(null);

  const user = useMemo(() => {
    return {
      role: (uiRole || 'teacher') as 'admin' | 'teacher' | 'student' | 'parent',
      name: me?.full_name || 'Utilizator',
      avatar: me?.avatar_url || undefined,
    };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const showSidebar = user.role === 'teacher' || user.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchNotificationsPaged({
        unread_only: tab === 'unread' ? true : undefined,
        page,
        page_size: pageSize,
      });
      setItems(Array.isArray(resp?.items) ? resp.items : []);
      setTotal(Number((resp as any)?.total ?? 0) || 0);
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        setItems([]);
        setTotal(0);
        return;
      }
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, tab]);

  useEffect(() => {
    // UX: când intri pe pagina de notificări, le considerăm citite.
    (async () => {
      try {
        await markAllNotificationsRead();
      } catch (e: any) {
        if (isSubscriptionError(e)) {
          setUpgradeOpen(true);
          return;
        }
      }
    })();
  }, []);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    clearSelection();
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page]);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) return setPushEnabled(false);
      setPushEnabled(await getPushEnabledState());
    })();
  }, []);

  useEffect(() => {
    const raw = (searchParams.get('contact_submission_id') || '').trim();
    const id = raw ? Number(raw) : NaN;
    if (!Number.isFinite(id) || id <= 0) return;

    setContactOpen(true);
    setContactLoading(true);
    setContactDetail(null);

    (async () => {
      try {
        const resp = await api.get(`/api/contact/submissions/${id}`);
        setContactDetail(resp.data as any);
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut incarca mesajul.');
        navigate('/notifications', { replace: true });
        setContactOpen(false);
      } finally {
        setContactLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const closeContact = useCallback(() => {
    setContactOpen(false);
    navigate('/notifications', { replace: true });
  }, [navigate]);

  const downloadContactAttachment = useCallback(async (submissionId: number, a: ContactAttachment) => {
    try {
      const resp = await api.get(
        `/api/contact/submissions/${submissionId}/attachments/${encodeURIComponent(a.stored_name)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: a.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.original_name || a.stored_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut descarca fisierul.');
    }
  }, []);

  const filtered = items;
  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [pageSize, total]);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const n of filtered) next.add(n.id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = window.confirm(`Ștergi ${ids.length} notificări selectate?`);
    if (!ok) return;
    setLoading(true);
    try {
      await deleteNotifications(ids);
      clearSelection();
      const newTotal = Math.max(0, (total || 0) - ids.length);
      const lastPage = Math.max(1, Math.ceil(newTotal / pageSize));
      const nextPage = Math.min(page, lastPage);
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        await load();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async (n: NotificationItem) => {
    try {
      if (!n.is_read) {
        await markNotificationRead(n.id);
      }
    } catch {
      // ignore
    }
    const contactSubmissionId = extractContactSubmissionId(n);
    if (
      contactSubmissionId &&
      (!n.link || n.link === '/notifications' || n.link === '/notifications/' || n.link.startsWith('/notifications?'))
    ) {
      navigate(`/notifications?contact_submission_id=${contactSubmissionId}`);
      return;
    }
    if (n.link) {
      // Back-compat: older notifications used `/teme` but the UI lives in Class Management Hub.
      if (n.link === '/teme' || n.link.startsWith('/teme?')) {
        navigate('/class-management-hub?tab=teme');
        return;
      }
      // Back-compat: for students we prefer opening tabs inside student-dashboard.
      if (user.role === 'student' && n.link.startsWith('/class-management-hub')) {
        try {
          const u = new URL(n.link, window.location.origin);
          const tab = (u.searchParams.get('tab') || '').trim();
          const classId = (u.searchParams.get('classId') || '').trim();
          if (tab && ['teme', 'cursuri', 'orar', 'note', 'prezentare', 'profesori'].includes(tab)) {
            const qs = new URLSearchParams();
            qs.set('tab', tab);
            if (classId) qs.set('classId', classId);
            navigate(`/student-dashboard?${qs.toString()}`);
            return;
          }
        } catch {
          // ignore
        }
      }
      navigate(n.link);
    }
  };

  return (
    <>
      <Helmet>
        <title>Notificări - Scola</title>
      </Helmet>

      <Header
        user={user}
        notificationCount={0}
        onNavigate={(path) => navigate(path)}
        onSignOut={() => signOut()}
      />

      <div className="min-h-screen bg-background pt-16">
        {showSidebar ? (
          <DashboardSidebar
            user={{ role: user.role }}
            isCollapsed={isSidebarCollapsed}
            onNavigate={(path) => navigate(path)}
          />
        ) : null}

        <main
          className={`transition-all duration-300 ${
            showSidebar ? (isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64') : ''
          }`}
        >
          <div className="p-6 max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsSidebarCollapsed((p) => !p)}
                  className="hidden lg:flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-colors"
                  aria-label="Comută bara laterală"
                >
                  <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Notificări</h1>
                  <p className="text-sm text-muted-foreground">Ultimele alerte și activități relevante.</p>
                </div>
              </div>

              <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                <Button variant="outline" iconName="RefreshCw" onClick={load} disabled={loading}>
                  Reîncarcă
                </Button>
                {isPushSupported() ? (
                  <Button
                    variant={pushEnabled ? 'outline' : 'default'}
                    iconName={pushEnabled ? 'BellOff' : 'BellRing'}
                    disabled={pushBusy}
                    onClick={async () => {
                      setPushBusy(true);
                      try {
                        if (pushEnabled) {
                          await disablePush();
                          setPushEnabled(false);
                          alert('Notificările push au fost dezactivate pe acest dispozitiv.');
                        } else {
                          const res = await enablePush();
                          if (!res.ok) {
                            alert(res.detail || 'Nu am putut activa push.');
                          } else {
                            setPushEnabled(true);
                            alert('Notificările push au fost activate (desktop).');
                          }
                        }
                      } finally {
                        setPushBusy(false);
                      }
                    }}
                  >
                    {pushEnabled ? 'Push: ON' : 'Push: OFF'}
                  </Button>
                ) : null}
                {isPushSupported() && pushEnabled ? (
                  <Button
                    variant="outline"
                    iconName="Send"
                    disabled={pushBusy}
                    onClick={async () => {
                      setPushBusy(true);
                      try {
                        const resp = await api.post('/api/push/test', {});
                        const sent = Number(resp.data?.sent ?? 0) || 0;
                        const targets = Number(resp.data?.targets ?? 0) || 0;
                        alert(
                          `Test push trimis: ${sent}/${targets}. Daca nu apare notificarea, verifica permisiunile de notificari + setarile de baterie.`
                        );
                      } catch (e: any) {
                        alert(e?.response?.data?.detail || 'Nu am putut trimite test push.');
                      } finally {
                        setPushBusy(false);
                      }
                    }}
                  >
                    Test push
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  iconName={selectMode ? 'X' : 'CheckSquare'}
                  onClick={() => {
                    setSelectMode((p) => !p);
                    clearSelection();
                  }}
                  disabled={loading}
                >
                  {selectMode ? 'Anulează' : 'Selectează'}
                </Button>
                {selectMode ? (
                  <>
                    <Button
                      variant="outline"
                      iconName="ListChecks"
                      onClick={selectAllFiltered}
                      disabled={loading || filtered.length === 0}
                    >
                      Selectează tot
                    </Button>
                    <Button
                      variant="destructive"
                      iconName="Trash2"
                      onClick={() => void handleDeleteSelected()}
                      disabled={loading || selectedCount === 0}
                    >
                      Șterge ({selectedCount})
                    </Button>
                  </>
                ) : null}
                <Button
                  variant="default"
                  iconName="CheckCheck"
                  onClick={async () => {
                    await markAllNotificationsRead();
                    await load();
                  }}
                  disabled={items.every((n) => n.is_read)}
                >
                  Marchează citite
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setTab('all')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                  tab === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground'
                }`}
              >
                Toate
              </button>
              <button
                onClick={() => setTab('unread')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                  tab === 'unread' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground'
                }`}
              >
                Necitite
              </button>
              <div className="ml-auto text-xs text-muted-foreground">
                Pagina <span className="text-foreground">{page}</span>/<span className="text-foreground">{totalPages}</span> • Total{' '}
                <span className="text-foreground">{total}</span>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-6 text-sm text-muted-foreground">Se încarcă...</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 flex flex-col items-center justify-center">
                  <Icon name="BellOff" size={48} className="text-muted-foreground mb-3" />
                  <div className="text-sm text-muted-foreground text-center">
                    Nu există notificări {tab === 'unread' ? 'necitite' : ''}.
                  </div>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {filtered.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => {
                          if (selectMode) {
                            toggleSelected(n.id);
                            return;
                          }
                          void handleOpen(n);
                        }}
                        className={`w-full text-left p-4 hover:bg-muted/40 transition-colors ${
                          !n.is_read ? 'bg-muted/30' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {selectMode ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(n.id)}
                              onChange={() => toggleSelected(n.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 h-4 w-4 accent-primary"
                              aria-label="Selectează notificarea"
                            />
                          ) : null}
                          <Icon name="Bell" size={18} className="text-muted-foreground mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-foreground truncate">{n.title}</div>
                              <div className="text-xs text-muted-foreground whitespace-nowrap">
                                {new Date(n.created_at).toLocaleString()}
                              </div>
                            </div>
                            <div className="mt-1 text-sm text-muted-foreground line-clamp-2">{n.content}</div>
                            {n.link || extractContactSubmissionId(n) ? (
                              <div className="mt-2 text-xs text-primary inline-flex items-center gap-1">
                                <span>Deschide</span>
                                <Icon name="ChevronRight" size={14} />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={loading || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Înapoi
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={loading || page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Înainte
              </Button>
            </div>
          </div>
        </main>

        {contactOpen ? (
          <Modal title={contactDetail?.subject ? `Raportare abuz • ${contactDetail.subject}` : 'Raportare abuz'} onClose={closeContact}>
            {contactLoading ? (
              <div className="text-sm text-muted-foreground">Se incarca...</div>
            ) : !contactDetail ? (
              <div className="text-sm text-muted-foreground">Mesaj indisponibil.</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">Data</div>
                    <div className="text-foreground">{fmtDateTime(contactDetail.created_at)}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground">De la</div>
                    <div className="text-foreground">{contactDetail.user?.email || contactDetail.contact_email || '—'}</div>
                  </div>
                </div>

                <div className="text-sm">
                  <div className="text-xs text-muted-foreground mb-1">Mesaj</div>
                  <div className="whitespace-pre-wrap border border-border rounded-lg p-3 bg-muted/10 text-foreground">
                    {contactDetail.message || '—'}
                  </div>
                </div>

                {Array.isArray(contactDetail.attachments) && contactDetail.attachments.length > 0 ? (
                  <div className="text-sm">
                    <div className="text-xs text-muted-foreground mb-2">Atasamente</div>
                    <div className="space-y-2">
                      {contactDetail.attachments.map((a) => (
                        <div
                          key={a.stored_name}
                          className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="text-foreground truncate">{a.original_name || a.stored_name}</div>
                            <div className="text-xs text-muted-foreground">{fmtBytes(a.size)}</div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            onClick={() => void downloadContactAttachment(contactDetail.id, a)}
                          >
                            Descarca
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </Modal>
        ) : null}

        <SubscriptionRequiredModal open={upgradeOpen} feature="notifications" onClose={() => setUpgradeOpen(false)} />
      </div>
    </>
  );
}
